import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  UserPlus, Trash2, KeyRound, Save, History, Loader2, Building2, Globe2,
  Users as UsersIcon, Activity, ShieldCheck, Clock, LogIn, Mail, MessageSquare,
  Video, CircleUserRound, Sparkles,
} from "lucide-react";

const ROLES = ["Admin", "Organizer", "Participant", "Reviewer"];

const CAT_LABEL = { auth: "Auth", email: "Emails", help: "Help chats", meeting: "Meetings" };
const CAT_CHIP = {
  auth: "tone-powder",
  email: "bg-amber-100 text-amber-800",
  help: "bg-violet-100 text-violet-700",
  meeting: "bg-slate-100 text-slate-700",
};
const CAT_ICON = { auth: LogIn, email: Mail, help: MessageSquare, meeting: Video };
const EVENT_LABEL = {
  login_success: "Signed in", login_failed: "Failed sign-in", login_blocked_inactive: "Blocked (inactive)",
  register_completed: "Account created", email_verification_sent: "Verification link sent",
  email_verified: "Email verified", password_changed: "Password changed",
  email_sent: "Email sent", help_message: "Asked Yanthra AI", meeting_accessed: "Opened meeting",
  assistant_command: "Ran a voice action",
};
const prettyEvent = (e) => EVENT_LABEL[e] || (e || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

/* ---------------- Activity dialog (full log per user) ---------------- */
function ActivityDialog({ u }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(false);

  async function load(c) {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/activity${c && c !== "all" ? `?category=${c}` : ""}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  function onOpenChange(o) { setOpen(o); if (o) { setCat("all"); load("all"); } }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger data-testid={`admin-activity-trigger-${u.id}`} asChild>
        <Button data-testid={`admin-user-activity-${u.id}`} size="sm" variant="outline"
          className="rounded-full h-8 w-8 p-0 hover:text-[hsl(var(--navy))]" title="View activity log">
          <History className="w-3.5 h-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="activity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl">
            <span className="w-9 h-9 rounded-xl text-sm bg-gradient-to-br from-[hsl(var(--navy))] to-[hsl(var(--cyan))] text-white font-bold grid place-items-center">{initials(u.name)}</span>
            Activity · {u.name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {["all", "auth", "email", "help", "meeting"].map((c) => (
            <button key={c} data-testid={`activity-cat-${c}`} onClick={() => { setCat(c); load(c); }}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${cat === c ? "text-white border-transparent" : "text-foreground/70 border-border hover:bg-secondary"}`}
              style={cat === c ? { background: "hsl(var(--navy))" } : {}}>
              {c === "all" ? "All" : CAT_LABEL[c]}{data?.counts && c !== "all" && data.counts[c] ? ` (${data.counts[c]})` : ""}
            </button>
          ))}
        </div>
        <div className="max-h-[52vh] overflow-y-auto mt-2 -mx-1 px-1">
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : !data || data.items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">No activity recorded yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {(data.items || []).map((it) => {
                const Icon = CAT_ICON[it.category] || Activity;
                return (
                  <li key={it.id} data-testid="activity-row" className="flex items-start gap-3 rounded-xl border border-border bg-card px-3 py-2.5">
                    <span className={`mt-0.5 shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${CAT_CHIP[it.category] || "bg-slate-100 text-slate-600"}`}>
                      <Icon className="w-3 h-3" />{CAT_LABEL[it.category] || it.category}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{prettyEvent(it.event)}</div>
                      {it.detail && <div className="text-xs text-muted-foreground truncate">{it.detail}</div>}
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {new Date(it.created_at).toLocaleString()}{it.ip ? ` · ${it.ip}` : ""}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Member row (inline edit controls preserved) ---------------- */
function MemberRow({ u, onSaved }) {
  const [quota, setQuota] = useState(u.meeting_quota ?? "");
  const [validUntil, setValidUntil] = useState(u.valid_until || "");
  const [role, setRole] = useState(u.role);
  const [active, setActive] = useState(u.is_active !== false);
  const [busy, setBusy] = useState(false);
  const la = u.last_activity;

  async function save() {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${u.id}`, {
        role, is_active: active,
        meeting_quota: quota === "" ? null : Number(quota),
        valid_until: validUntil || null,
      });
      toast.success("Updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  async function resetPassword() {
    const temp = prompt("New temp password (≥6 chars). User will be forced to change on next login:");
    if (!temp || temp.length < 6) return;
    try {
      await api.post(`/admin/users/${u.id}/reset-password`, { temp_password: temp });
      toast.success("Password reset — share the temp with the user");
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function remove() {
    if (!window.confirm(`Delete user ${u.email}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Deleted");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid={`admin-user-row-${u.id}`}
      className="grid grid-cols-1 md:grid-cols-[minmax(200px,2fr)_132px_104px_152px_auto] md:items-center gap-3 md:gap-4 px-4 py-3.5 border-t border-border/70 hover:bg-secondary/30 transition-colors">
      {/* identity */}
      <div className="flex items-start gap-3 min-w-0">
        <span className="shrink-0 w-10 h-10 rounded-2xl text-xs bg-gradient-to-br from-[hsl(var(--navy))] to-[hsl(var(--cyan))] text-white font-bold grid place-items-center shadow-sm">
          {initials(u.name)}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm truncate">{u.name}</span>
            {u.org_role === "OrgAdmin" && (
              <span className="tone-lavender inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide">
                <ShieldCheck className="w-3 h-3" /> Admin
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate">{u.email}</div>
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            {la ? (
              <>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${CAT_CHIP[la.category] || "bg-slate-100 text-slate-600"}`}>
                  {(() => { const I = CAT_ICON[la.category] || Activity; return <I className="w-2.5 h-2.5" />; })()}
                  {prettyEvent(la.event)}
                </span>
                <span>· {relTime(la.created_at)}</span>
              </>
            ) : (
              <span className="inline-flex items-center gap-1 text-muted-foreground/70"><Clock className="w-3 h-3" /> No activity yet</span>
            )}
          </div>
        </div>
      </div>

      {/* role */}
      <div>
        <div className="md:hidden eyebrow mb-1">Role</div>
        <Select data-testid={`admin-role-select-${u.id}`} value={role} onValueChange={setRole}>
          <SelectTrigger data-testid={`admin-role-trigger-${u.id}`} className="h-9 rounded-full w-full"><SelectValue /></SelectTrigger>
          <SelectContent>{ROLES.map((r) => <SelectItem key={r} data-testid={`admin-role-${u.id}-${r}`} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* quota */}
      <div>
        <div className="md:hidden eyebrow mb-1">Quota</div>
        <Input data-testid={`admin-quota-${u.id}`} type="number" min={0} value={quota}
          onChange={(e) => setQuota(e.target.value)} placeholder="∞" className="h-9 w-full rounded-full text-center" />
        <div className="text-[10px] text-muted-foreground mt-0.5 text-center">used {u.meetings_used ?? 0}</div>
      </div>

      {/* valid until + active */}
      <div className="space-y-1.5">
        <div className="md:hidden eyebrow mb-1">Valid until</div>
        <Input data-testid={`admin-valid-until-${u.id}`} type="date" value={validUntil}
          onChange={(e) => setValidUntil(e.target.value)} className="h-9 rounded-full w-full" />
        <label className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold cursor-pointer ${active ? "tone-sage" : "tone-blush"}`}>
          <input data-testid={`admin-active-${u.id}`} type="checkbox" checked={active}
            onChange={(e) => setActive(e.target.checked)} className="accent-current w-3 h-3" />
          {active ? "Active" : "Inactive"}
        </label>
      </div>

      {/* actions */}
      <div className="flex md:justify-end items-center gap-1">
        <ActivityDialog u={u} />
        <Button data-testid={`admin-user-save-${u.id}`} onClick={save} disabled={busy} size="sm" variant="outline"
          className="rounded-full h-8 w-8 p-0 hover:text-[hsl(var(--navy))]" title="Save changes">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        </Button>
        <Button data-testid={`admin-user-reset-${u.id}`} onClick={resetPassword} size="sm" variant="outline"
          className="rounded-full h-8 w-8 p-0 hover:text-[hsl(var(--navy))]" title="Reset password">
          <KeyRound className="w-3.5 h-3.5" />
        </Button>
        <Button data-testid={`admin-user-delete-${u.id}`} onClick={remove} size="sm" variant="outline"
          className="rounded-full h-8 w-8 p-0 text-rose-600 hover:text-rose-700" title="Delete">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Organisation card ---------------- */
function OrgCard({ org, onSaved, index }) {
  const suspended = (org.status || "active") !== "active";
  const region = org.region || org.billing_country;
  return (
    <section data-testid={`admin-org-card-${org.id}`}
      className="rounded-3xl border border-border/80 bg-card shadow-card overflow-hidden yl-fade-up"
      style={{ animationDelay: `${Math.min(index * 70, 420)}ms` }}>
      {/* org header */}
      <div className="p-5 sm:p-6 bg-gradient-to-br from-[hsl(var(--navy))]/[0.04] to-[hsl(var(--cyan))]/[0.06] border-b border-border/70">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <span className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-[hsl(var(--navy))] to-[hsl(var(--cyan))] text-white grid place-items-center shadow-sm">
              <Building2 className="w-6 h-6" strokeWidth={1.8} />
            </span>
            <div className="min-w-0">
              <h2 className="font-heading text-xl sm:text-2xl tracking-tight truncate">{org.name}</h2>
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                {region && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-secondary text-foreground/70">
                    <Globe2 className="w-3 h-3" /> {region}
                  </span>
                )}
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${suspended ? "tone-blush" : "tone-sage"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${suspended ? "bg-rose-500" : "bg-emerald-500 yl-pulse-dot"}`} />
                  {suspended ? "Suspended" : "Active"}
                </span>
                {org.plan && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold tone-lavender capitalize">
                    <Sparkles className="w-3 h-3" /> {org.plan}
                  </span>
                )}
              </div>
            </div>
          </div>
          {org.last_active_at && (
            <div className="text-right">
              <div className="eyebrow">Last active</div>
              <div className="text-sm font-semibold mt-0.5">{relTime(org.last_active_at)}</div>
            </div>
          )}
        </div>

        {/* org mini-stats */}
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2.5">
          <OrgStat icon={UsersIcon} label="Members" value={org.member_count} />
          <OrgStat icon={CircleUserRound} label="Active" value={org.active_count} />
          <OrgStat icon={ShieldCheck} label="Admins" value={org.admin_count} />
          <OrgStat icon={Activity} label="Actions / 7d" value={org.activities_7d} className="hidden sm:flex" />
        </div>
      </div>

      {/* member table header (desktop) */}
      <div className="hidden md:grid grid-cols-[minmax(200px,2fr)_132px_104px_152px_auto] gap-4 px-4 py-2.5 bg-secondary/40">
        <div className="eyebrow">Member</div>
        <div className="eyebrow">Role</div>
        <div className="eyebrow text-center">Quota</div>
        <div className="eyebrow">Validity &amp; status</div>
        <div className="eyebrow text-right">Actions</div>
      </div>

      {/* members */}
      <div>
        {(org.members || []).map((m) => <MemberRow key={m.id} u={m} onSaved={onSaved} />)}
        {(!org.members || org.members.length === 0) && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">No members in this workspace.</div>
        )}
      </div>
    </section>
  );
}

function OrgStat({ icon: Icon, label, value, className = "" }) {
  return (
    <div className={`flex items-center gap-2.5 rounded-2xl yl-glass px-3 py-2 ${className}`}>
      <span className="w-8 h-8 rounded-xl bg-[hsl(var(--navy))]/[0.06] text-[hsl(var(--navy))] grid place-items-center">
        <Icon className="w-4 h-4" />
      </span>
      <div className="leading-none">
        <div className="text-lg font-bold">{value}</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5">{label}</div>
      </div>
    </div>
  );
}

/* ---------------- KPI strip ---------------- */
function KpiCard({ icon: Icon, label, value, accent }) {
  return (
    <div data-testid={`admin-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-3xl border border-border/80 bg-card shadow-card p-5 flex items-center gap-4 hover:shadow-lift transition-all">
      <span className="shrink-0 w-12 h-12 rounded-2xl grid place-items-center text-white shadow-sm" style={{ background: accent }}>
        <Icon className="w-6 h-6" strokeWidth={1.8} />
      </span>
      <div>
        <div className="font-heading text-3xl tracking-tight leading-none">{value}</div>
        <div className="eyebrow mt-1.5">{label}</div>
      </div>
    </div>
  );
}

/* ---------------- Recent activity feed ---------------- */
function RecentActivity({ items }) {
  const [cat, setCat] = useState("all");
  const filtered = (items || []).filter((it) => cat === "all" || it.category === cat);
  return (
    <aside data-testid="admin-recent-activity" className="rounded-3xl border border-border/80 bg-card shadow-card overflow-hidden lg:sticky lg:top-6">
      <div className="p-5 border-b border-border/70 bg-gradient-to-br from-[hsl(var(--navy))]/[0.04] to-transparent">
        <div className="eyebrow">Live</div>
        <h2 className="font-heading text-xl tracking-tight mt-1 flex items-center gap-2">
          <Activity className="w-5 h-5 text-[hsl(var(--cyan))]" /> Recent activity
        </h2>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {["all", "auth", "email", "help", "meeting"].map((c) => (
            <button key={c} data-testid={`feed-cat-${c}`} onClick={() => setCat(c)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${cat === c ? "text-white border-transparent" : "text-foreground/70 border-border hover:bg-secondary"}`}
              style={cat === c ? { background: "hsl(var(--navy))" } : {}}>
              {c === "all" ? "All" : CAT_LABEL[c]}
            </button>
          ))}
        </div>
      </div>
      <div className="max-h-[70vh] overflow-y-auto divide-y divide-border/60">
        {filtered.length === 0 && (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">No activity yet.</div>
        )}
        {filtered.map((it, i) => {
          const Icon = CAT_ICON[it.category] || Activity;
          return (
            <div key={it.id || i} data-testid="admin-activity-feed-item"
              className="flex items-start gap-3 px-5 py-3 hover:bg-secondary/30 transition-colors yl-fade-up"
              style={{ animationDelay: `${Math.min(i * 35, 350)}ms` }}>
              <span className={`shrink-0 w-8 h-8 rounded-xl grid place-items-center ${CAT_CHIP[it.category] || "bg-slate-100 text-slate-600"}`}>
                <Icon className="w-4 h-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm">
                  <span className="font-semibold">{it.user_name}</span>
                  <span className="text-muted-foreground"> · {prettyEvent(it.event)}</span>
                </div>
                {it.detail && <div className="text-xs text-muted-foreground truncate">{it.detail}</div>}
                <div className="text-[11px] text-muted-foreground mt-0.5">{relTime(it.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

/* ---------------- Add user dialog ---------------- */
function AddUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", temp_password: "", role: "Organizer", meeting_quota: "", valid_until: "" });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", {
        ...form,
        meeting_quota: form.meeting_quota === "" ? null : Number(form.meeting_quota),
        valid_until: form.valid_until || null,
      });
      toast.success(`User created — share temp password '${form.temp_password}' with them`);
      setOpen(false);
      setForm({ name: "", email: "", temp_password: "", role: "Organizer", meeting_quota: "", valid_until: "" });
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger data-testid="admin-user-add-trigger" asChild>
        <Button data-testid="admin-user-add-btn" className="rounded-full h-10 px-5 text-white hover:opacity-90 transition-opacity"
          style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}>
          <UserPlus className="w-4 h-4 mr-1.5" /> Add user
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Add user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="admin-user-add-form">
          <div><Label className="eyebrow">Name</Label><Input data-testid="au-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div><Label className="eyebrow">Email</Label><Input data-testid="au-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
          <div>
            <Label className="eyebrow">Temp password (share with user)</Label>
            <Input data-testid="au-temp" value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} required minLength={6} />
            <p className="text-[10px] text-muted-foreground mt-1">Min 6 characters. User will be forced to change on first login.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="eyebrow">Role</Label>
              <Select data-testid="au-role-select" value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="au-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} data-testid={`au-role-${r}`} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Meeting quota (blank = ∞)</Label>
              <Input data-testid="au-quota" type="number" min={0} value={form.meeting_quota} onChange={(e) => setForm({ ...form, meeting_quota: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="eyebrow">Valid until (blank = no expiry)</Label>
            <Input data-testid="au-valid" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
          </div>
          <DialogFooter>
            <Button data-testid="au-submit" type="submit" disabled={busy} className="rounded-full">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Create user
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Page ---------------- */
export default function AdminUsers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/org-overview").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const totals = data?.totals || { organizations: 0, members: 0, active: 0, activities_7d: 0 };
  const orgs = data?.organizations || [];

  return (
    <div data-testid="admin-users-root" className="p-6 md:p-8 lg:p-12 space-y-7">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Admin · workspace management</div>
          <h1 className="font-heading text-4xl tracking-tight mt-2">Organisations.</h1>
          <p className="text-sm text-muted-foreground mt-1">Every workspace, its people, roles and what they’ve been doing — at a glance.</p>
        </div>
        <AddUserDialog onCreated={load} />
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Building2} label="Organisations" value={totals.organizations} accent="linear-gradient(135deg,#0f2557,#3b6198)" />
        <KpiCard icon={UsersIcon} label="Total members" value={totals.members} accent="linear-gradient(135deg,#0f2557,#29abe2)" />
        <KpiCard icon={CircleUserRound} label="Active members" value={totals.active} accent="linear-gradient(135deg,#0e8f6e,#34d399)" />
        <KpiCard icon={Activity} label="Actions this week" value={totals.activities_7d} accent="linear-gradient(135deg,#7c5cbf,#29abe2)" />
      </div>

      {loading ? (
        <div className="py-24 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 space-y-6">
            {orgs.length === 0 && (
              <div className="rounded-3xl border border-border bg-card shadow-card py-16 text-center text-muted-foreground">No organisations yet.</div>
            )}
            {orgs.map((org, i) => <OrgCard key={org.id} org={org} index={i} onSaved={load} />)}
          </div>
          <div className="lg:col-span-1">
            <RecentActivity items={data?.recent_activity} />
          </div>
        </div>
      )}
    </div>
  );
}
